import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_INSTITUTIONFINANCE_LOAD_BY_INSTITUTION_ID,
    ACTION_INSTITUTIONFINANCE_LOAD_BY_ID,
    ACTION_INSTITUTIONFINANCE_LOAD_FAILURE,
    ACTION_INSTITUTIONFINANCE_LOAD_SUCCESS,

    ACTION_INSTITUTIONFINANCE_SAVE,
    ACTION_INSTITUTIONFINANCE_SAVE_SUCCESS,
    ACTION_INSTITUTIONFINANCE_SAVE_FAILURE,

    ACTION_INSTITUTIONFINANCE_VALIDATE,
    ACTION_INSTITUTIONFINANCE_VALIDATE_SUCCESS,
    ACTION_INSTITUTIONFINANCE_VALIDATE_FAILURE,

    ACTION_INSTITUTIONFINANCE_CREATE,
    ACTION_INSTITUTIONFINANCE_CREATE_SUCCESS,
    ACTION_INSTITUTIONFINANCE_CREATE_FAILURE,

    ACTION_INSTITUTIONFINANCE_SYNC,
    ACTION_INSTITUTIONFINANCE_SYNC_SUCCESS,
    ACTION_INSTITUTIONFINANCE_SYNC_FAILURE,

    ACTION_INSTITUTIONFINANCE_CLEAR,
    ACTION_INSTITUTIONFINANCE_FILTER,

    InstitutionFinanceAction
} from '../actions/institutionFinance';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface IInstitutionFinanceState {
    formData: Dtos.InstitutionFinance | Dtos.InstitutionFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    validateState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.InstitutionFinance[];
    formLength: number;
}

const initialState: IInstitutionFinanceState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: IInstitutionFinanceState, _isLoading: boolean): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: IInstitutionFinanceState,
    formData: Dtos.InstitutionFinance | Dtos.InstitutionFinance[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: IInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: IInstitutionFinanceState, formFilter: IFormFilter): IInstitutionFinanceState {

    const items: Dtos.InstitutionFinance[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.taxInvoiceName.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: IInstitutionFinanceState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: IInstitutionFinanceState, propertiesToClear?: string[]): IInstitutionFinanceState {

    if (!propertiesToClear) {
        const newState: IInstitutionFinanceState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: IInstitutionFinanceState, _isSaving: boolean): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: IInstitutionFinanceState,
    formData: Dtos.InstitutionFinance | Dtos.InstitutionFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: IInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function validating(state: IInstitutionFinanceState, _isValidating: boolean): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: IInstitutionFinanceState,
    formData: Dtos.InstitutionFinance | Dtos.InstitutionFinance[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: IInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: IInstitutionFinanceState, _isCreating: boolean): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: IInstitutionFinanceState,
    formData: Dtos.InstitutionFinance,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: IInstitutionFinanceState, responseStatus: Dtos.ResponseStatus): IInstitutionFinanceState {
    const newState: IInstitutionFinanceState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const InstitutionFinanceReducer: Reducer<IInstitutionFinanceState> = (state: IInstitutionFinanceState = initialState, action: InstitutionFinanceAction) => {
    switch (action.type) {
        case ACTION_INSTITUTIONFINANCE_LOAD_BY_INSTITUTION_ID:
        case ACTION_INSTITUTIONFINANCE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_INSTITUTIONFINANCE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_INSTITUTIONFINANCE_SAVE:
            return saving(state, true);
        case ACTION_INSTITUTIONFINANCE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_VALIDATE:
            return validating(state, true);
        case ACTION_INSTITUTIONFINANCE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_CREATE:
            return creating(state, true);
        case ACTION_INSTITUTIONFINANCE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_INSTITUTIONFINANCE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_FILTER:
            return filter(state, action.formFilter);
        case ACTION_INSTITUTIONFINANCE_SYNC:
            return loading(state, true);
        case ACTION_INSTITUTIONFINANCE_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_INSTITUTIONFINANCE_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default InstitutionFinanceReducer;